<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import AtlasButton from '../Common/AtlasButton.vue';
const isSticky = ref(false);
const forceExpanded = ref(false);

const props = defineProps<{
    selectedScheme?: any
    action?: string
}>()

const isMta = computed(() => (props.action ?? '').toLowerCase().includes('mta'))

const premium = computed(() =>
    isMta.value ? props.selectedScheme?.ProRataPremium : props.selectedScheme?.QuotedPremium
)

const summaryBarAnchor = ref<HTMLElement | null>(null);
let stickyObserver: IntersectionObserver | null = null;

const desktopQuery = window.matchMedia('(min-width: 992px)');

const startObserving = () => {
    if (stickyObserver || !summaryBarAnchor.value) return;
    stickyObserver = new IntersectionObserver(
        ([entry]) => {
            // Stick only when the anchor has scrolled ABOVE the top of the viewport.
            // !isIntersecting alone is also true when the anchor sits BELOW the fold
            // (tall content at the top of the page), which would stick the bar too early.
            const scrolledAboveTop = entry.boundingClientRect.top < 0;
            isSticky.value = !entry.isIntersecting && scrolledAboveTop;
            if (!isSticky.value) forceExpanded.value = false;
        },
        { threshold: 0 }
    );
    stickyObserver.observe(summaryBarAnchor.value);
};

const stopObserving = () => {
    stickyObserver?.disconnect();
    stickyObserver = null;
    isSticky.value = false;
    forceExpanded.value = false;
};

const handleBreakpointChange = () => {
    if (desktopQuery.matches) startObserving();
    else stopObserving();
};

onMounted(() => {
    handleBreakpointChange();
    desktopQuery.addEventListener('change', handleBreakpointChange);
});

onUnmounted(() => {
    desktopQuery.removeEventListener('change', handleBreakpointChange);
    stopObserving();
});

const insurerName = computed(() =>
    (props.selectedScheme?.Insurer?.Description ?? '').toUpperCase()
)

const selectedAddonsTotal = computed(() => {
    let total = 0
    for (const addon of props.selectedScheme?.Addons ?? []) {
        if (addon.IsSelected && !addon.IsIncluded) {
            total += parseFloat(addon.TotalPayable ?? 0)
        }
    }
    for (const group of props.selectedScheme?.GroupedAddons ?? []) {
        for (const addon of group.GroupAddons ?? []) {
            if (addon.IsSelected && !addon.IsIncluded) {
                total += parseFloat(addon.TotalPayable ?? 0)
            }
        }
    }
    return total
})

const totalPayable = computed(() => parseFloat(premium.value?.TotalPayable ?? 0) + selectedAddonsTotal.value)
</script>

<template>
        <!-- Summary Bar Container -->
        <div id="summary-trigger" class="mb-3" style="min-height: 240px;">
            <div
            class="sticky-summary-bar d-flex flex-column z-1 w-100 position-relative"
            :class="{'position-fixed is-sticky bg-white px-4 py-2': isSticky, 'is-expanded': forceExpanded}">
                
                <!-- Minimal View (Sticky Only) -->
                <div v-if="isSticky && !forceExpanded" class="minimal-sticky-bar d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                    <div v-if="props.selectedScheme" class="d-flex">
                        <span class="h5 mb-0 mr-3">Selected Scheme</span>
                        <span class="h5 mb-0 text-dark">{{ insurerName }}</span>
                    </div>
                    <div v-else class="mr-4 h5 mb-0">Please select a scheme...</div>

                    <div class="border-left mx-4"></div>

                    <div class="d-flex align-items-center">
                        <span class="mr-3 h5 mb-0">Total Payable</span>
                        <span class="fs-28 text-dark mb-0 font-weight-bold">£{{ props.selectedScheme ? totalPayable.toFixed(2) : '0.00' }}</span>
                    </div>
                    </div>

                    <div>
                        <AtlasButton
                            variant="primary"
                            :outline="true"
                            @click="forceExpanded = true" 
                        >
                            <em class="far fa-chevron-down"></em>
                            <span class="ml-2">Expand Summary</span>
                        </AtlasButton>

                        <slot name="summary-bar-actions" />
                    </div>
                </div>

                    <!-- Full View (Interactive Summary) -->
                <div v-show="!isSticky || forceExpanded" class="summary-content flex-column mx-auto w-100" :class="{'d-flex': isSticky && forceExpanded}">

                    <slot name="summary-bar-content" />

                    <!-- Collapse Tab (Only visible when sticky) -->
                    <div v-if="isSticky && forceExpanded" class="align-self-end">
                        <AtlasButton 
                            variant="primary"
                            :outline="true"
                            @click="forceExpanded = false"
                            >
                            <em class="far fa-chevron-up"></em>
                            <span class="ml-2">Collapse Summary</span>
                        </AtlasButton>
                    </div>
                </div>
            </div>
        </div>

        <!-- Anchor: when this scrolls out of view, the summary bar becomes sticky -->
        <div ref="summaryBarAnchor" aria-hidden="true" style="height: 1px;"></div>
    </template>